import type { AxiosResponse } from "axios";
import React from "react";
import { FormattedMessage } from "react-intl";
import { SubmissionError } from "redux-form";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import type { AuthenticationToken } from "src/authentication/objects/AuthenticationToken";
import { MERCHANT_API_PATH } from "src/confs/APIConfig";
import { MERCHANT_MOBILE_VIRTUAL_NUMBER } from "src/merchants/forms/MerchantForm";
import { Merchant } from "src/merchants/objects/Merchant";
import {
  APIGetAxios,
  APIPostAxios,
  APIPutAxios,
  type FormValidationError,
  type FormValidationErrorHandler,
  TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
} from "src/shared/actions/FetchActions";
import { removeEmptyStringsFromObj } from "src/shared/helpers/removeEmptyStringsFromObj";

const handleMerchantValidationErrors: FormValidationErrorHandler = (
  response: AxiosResponse,
  error: FormValidationError,
  fieldName?: string,
) => {
  if (response.status === 400 && fieldName === MERCHANT_MOBILE_VIRTUAL_NUMBER) {
    if (error.code === "BAD_REQUEST") {
      throw new SubmissionError({
        [fieldName]: (
          <FormattedMessage id="merchants.validation.numberProvisioning.unableToProvision" />
        ),
      });
    } else if (error.code === "ALREADY_EXISTS") {
      throw new SubmissionError({
        [fieldName]: (
          <FormattedMessage id="merchants.validation.numberProvisioning.alreadyProvisioned" />
        ),
      });
    }
  }
};

export function GetMerchants(
  merchantGroupUUID?: string,
  authenticationToken?: AuthenticationToken,
) {
  return async (dispatch: ThunkDispatchType) => {
    let url: string = `${MERCHANT_API_PATH}`;
    if (merchantGroupUUID) {
      url += `?merchantGroupUUID=${merchantGroupUUID}`;
    }

    const fetchResult = await dispatch(
      APIGetAxios<Merchant[]>(
        url,
        undefined,
        authenticationToken,

        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data.map((merchantJson) => {
      return new Merchant(merchantJson);
    });
  };
}

export function GetMerchantByMerchantUUID(merchantUUID: string) {
  return async (dispatch: ThunkDispatchType) => {
    const fetchResult = await dispatch(
      APIGetAxios<Merchant>(
        `${MERCHANT_API_PATH}/${merchantUUID}`,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return new Merchant(fetchResult.data);
  };
}

export function CreateMerchant(merchant: Merchant) {
  return async (dispatch: ThunkDispatchType) => {
    const jsonObj = merchant.toJSON();
    removeEmptyStringsFromObj(jsonObj);
    const fetchResult: AxiosResponse = await dispatch(
      APIPostAxios<string>(
        `${MERCHANT_API_PATH}`,
        jsonObj,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
        handleMerchantValidationErrors,
      ),
    );
    const response: { merchantUUID: string } = fetchResult.data;
    return response.merchantUUID;
  };
}

export function EditMerchant(merchant: Merchant) {
  return async (dispatch: ThunkDispatchType) => {
    const jsonObj = merchant.toJSON();
    removeEmptyStringsFromObj(jsonObj);
    const fetchResult = await dispatch(
      APIPutAxios<{}>(
        `${MERCHANT_API_PATH}/${merchant.merchantUUID}`,
        jsonObj,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
        handleMerchantValidationErrors,
      ),
    );
    return fetchResult.data;
  };
}

export function UploadMerchantLogo(
  logoFile: File,
  onUploadProgress?: (progressEvent: ProgressEvent) => void,
) {
  return async (dispatch: ThunkDispatchType) => {
    const formData: FormData = new FormData();
    formData.append("file", logoFile);

    const response = await dispatch(
      APIPostAxios<{ utid: string; logoUrl: string }>(
        `${MERCHANT_API_PATH}/action/_logo`,
        formData,
        {
          onUploadProgress,
        },
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return response.data;
  };
}

export const getAssignNumber = (countryCode: string) => {
  return async (dispatch: ThunkDispatchType) => {
    const fetchResult = await dispatch(
      APIPostAxios<{ mobileVirtualNumber: string }>(
        `${MERCHANT_API_PATH}/action/_number.assign`,
        { countryCode },
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data;
  };
};

export const deactivateMerchant = (merchantUuid: string, note: string) => {
  return async (dispatch: ThunkDispatchType) => {
    const fetchResult = await dispatch(
      APIPostAxios<void>(
        `${MERCHANT_API_PATH}/${merchantUuid}/action/_deactivate`,
        { note },
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data;
  };
};
